class MwFormSwitcher {
  constructor(selector = ".js-mw-form-switcher") {
    this.root = document.querySelector(selector);

    if (!this.root) return;

    this.form = this.root.closest("form");
    this.mwForm = this.root.closest(".mw_wp_form");

    /**
     * 現在のフォームモード保持用
     * general / recruit
     */
    this.modeInput = this.root.querySelector('input[name="contact_mode"]');

    /**
     * 自動返信先メールアドレス保持用
     */
    this.replyEmailInput = this.root.querySelector('input[name="reply_email"]');

    /**
     * お問い合わせ種別
     */
    this.radioInputs = Array.from(
      this.root.querySelectorAll('input[type="radio"][name="contact_type"]'),
    );

    /**
     * general / recruit の切り替え対象
     */
    this.fieldGroups = Array.from(
      this.root.querySelectorAll(".js-mw-form-fields"),
    );

    /**
     * 各フィールドが元々disabledだったか保持
     */
    this.disabledStates = new WeakMap();

    /**
     * contact_type → 表示モード
     */
    this.modeMap = {
      corporate: "general",
      other: "general",
      recruit: "recruit",
    };

    this.handleRadioChange = this.handleRadioChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.init();
  }

  /**
   * 初期化
   */
  init() {
    const mode = this.getInitialMode();

    this.setMode(mode);

    /**
     * 入力画面だけイベントを登録
     *
     * 確認画面ではMW WP Formが生成したhidden値などを
     * JS側で不用意に変更しない
     */
    if (this.isInputPage()) {
      this.bindEvents();

      /**
       * 「確認画面から戻る」等で値が復元されている場合に備え、
       * reply_emailも現在のメールアドレスと同期しておく
       */
      this.syncReplyEmail(mode);
    }
  }

  /**
   * MW WP Formの入力画面か判定
   */
  isInputPage() {
    if (this.mwForm?.classList.contains("mw_wp_form_input")) {
      return true;
    }

    return this.radioInputs.length > 0;
  }

  /**
   * 初期表示するモードを取得
   *
   * 優先順位
   *
   * 1. 入力画面のchecked済みcontact_type
   * 2. 確認画面等でhidden化されたcontact_type
   * 3. contact_mode
   * 4. general
   */
  getInitialMode() {
    /**
     * 入力画面
     */
    const checkedRadio = this.root.querySelector(
      'input[type="radio"][name="contact_type"]:checked',
    );

    if (checkedRadio) {
      return this.getModeFromContactType(checkedRadio.value);
    }

    /**
     * 確認画面
     *
     * MW WP Formがradioをhiddenへ変換した場合を想定
     */
    const contactTypeInput = this.root.querySelector(
      'input[name="contact_type"]:not([type="radio"])',
    );

    if (contactTypeInput?.value) {
      return this.getModeFromContactType(contactTypeInput.value);
    }

    /**
     * contact_modeをフォールバックとして利用
     */
    if (
      this.modeInput?.value === "general" ||
      this.modeInput?.value === "recruit"
    ) {
      return this.modeInput.value;
    }

    return "general";
  }

  /**
   * contact_typeから表示モードを取得
   */
  getModeFromContactType(contactType) {
    return this.modeMap[contactType] || "general";
  }

  /**
   * イベント登録
   */
  bindEvents() {
    this.radioInputs.forEach((radio) => {
      radio.addEventListener("change", this.handleRadioChange);
    });

    if (this.form) {
      this.form.addEventListener("submit", this.handleSubmit);
    }
  }

  /**
   * お問い合わせ種別変更
   */
  handleRadioChange(event) {
    const target = event.currentTarget;

    if (!target.checked) return;

    const mode = this.getModeFromContactType(target.value);

    this.setMode(mode);

    /**
     * モード変更時にもreply_emailを同期
     *
     * まだメール未入力なら空になるだけなので問題なし
     */
    this.syncReplyEmail(mode);
  }

  /**
   * フォーム送信直前
   *
   * contact_modeとreply_emailを
   * 最終的に必ず同期してからMW WP Formへ送る
   */
  handleSubmit() {
    const checkedRadio = this.root.querySelector(
      'input[type="radio"][name="contact_type"]:checked',
    );

    if (!checkedRadio) return;

    const mode = this.getModeFromContactType(checkedRadio.value);

    this.setMode(mode);
    this.syncReplyEmail(mode);
  }

  /**
   * general / recruit の表示切り替え
   */
  setMode(mode) {
    const activeMode = mode === "recruit" ? "recruit" : "general";

    /**
     * hiddenのcontact_modeを更新
     */
    if (this.modeInput) {
      this.modeInput.value = activeMode;
    }

    /**
     * JS・CSSから現在モードを判別可能にする
     */
    this.root.dataset.contactMode = activeMode;

    this.root.classList.toggle("--general", activeMode === "general");

    this.root.classList.toggle("--recruit", activeMode === "recruit");

    /**
     * 各フォームグループを切り替え
     */
    this.fieldGroups.forEach((group) => {
      const groupMode = group.dataset.contactMode;

      const isActive = groupMode === activeMode;

      group.hidden = !isActive;

      group.setAttribute("aria-hidden", String(!isActive));

      /**
       * 入力画面のみdisabledを制御
       *
       * 確認画面ではMW WP Form側のhidden値等を
       * disabledにしない
       */
      if (this.isInputPage()) {
        this.setFieldsDisabled(group, !isActive);
      }
    });
  }

  /**
   * 自動返信先メールアドレスを同期
   *
   * general
   * → general_email
   *
   * recruit
   * → recruit_email
   */
  syncReplyEmail(mode) {
    if (!this.replyEmailInput) return;

    const emailName = mode === "recruit" ? "recruit_email" : "general_email";

    const emailInput = this.root.querySelector(`input[name="${emailName}"]`);

    if (!emailInput) return;

    this.replyEmailInput.value = emailInput.value.trim();
  }

  /**
   * 指定グループ内のフォーム部品を
   * disabled / enabled切り替え
   */
  setFieldsDisabled(group, disabled) {
    const fields = group.querySelectorAll("input, textarea, select, button");

    fields.forEach((field) => {
      /**
       * 初回だけ元々のdisabled状態を保存
       */
      if (!this.disabledStates.has(field)) {
        this.disabledStates.set(field, field.disabled);
      }

      if (disabled) {
        field.disabled = true;
        return;
      }

      /**
       * 表示側へ戻したとき、
       * 元々のdisabled状態まで復元
       */
      field.disabled = this.disabledStates.get(field);
    });
  }

  /**
   * イベント解除
   */
  destroy() {
    this.radioInputs.forEach((radio) => {
      radio.removeEventListener("change", this.handleRadioChange);
    });

    if (this.form) {
      this.form.removeEventListener("submit", this.handleSubmit);
    }
  }
}

export default MwFormSwitcher;
